/**
 * NotificationBanner component - dismissible banner showing notification summary
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, AlertCircle, Calendar } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface NotificationBannerProps {
  overdueCount: number;
  upcomingCount: number;
  onDismiss?: () => void;
}

export const NotificationBanner: React.FC<NotificationBannerProps> = ({
  overdueCount,
  upcomingCount,
  onDismiss,
}) => {
  const [isDismissed, setIsDismissed] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleDismiss = () => {
    setIsDismissed(true);
    if (onDismiss) {
      onDismiss();
    }
  };

  const handleClickOverdue = () => {
    navigate('/tasks', {
      state: { filterType: 'overdue', filterUserId: user?.id, autoApply: true },
    });
  };

  const handleClickUpcoming = () => {
    navigate('/tasks', {
      state: { filterType: 'upcoming', filterUserId: user?.id, autoApply: true },
    });
  };

  // Don't show banner if no notifications or dismissed
  if (isDismissed || (overdueCount === 0 && upcomingCount === 0)) {
    return null;
  }

  // Determine banner color based on overdue count
  const bannerColorClasses =
    overdueCount > 0
      ? 'bg-error/10 dark:bg-error/20 border border-error/30 text-error'
      : 'bg-primary-50 border-primary-200 dark:bg-primary-900/20 dark:border-primary-800';

  return (
    <div
      className={`relative flex items-center justify-between rounded-lg border p-4 shadow-sm transition-all ${bannerColorClasses}`}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start space-x-3">
        {overdueCount > 0 ? (
          <AlertCircle className="h-5 w-5 text-error flex-shrink-0 mt-0.5" />
        ) : (
          <Calendar className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
        )}

        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            Task Notifications
          </p>
          <div className="mt-1 text-sm text-gray-700 dark:text-gray-300 space-y-1">
            {overdueCount > 0 && (
              <button
                onClick={handleClickOverdue}
                className="block hover:underline text-left"
              >
                <span className="font-semibold text-error">
                  {overdueCount}
                </span>{' '}
                overdue task{overdueCount !== 1 ? 's' : ''} requiring attention
              </button>
            )}
            {upcomingCount > 0 && (
              <button
                onClick={handleClickUpcoming}
                className="block hover:underline text-left"
              >
                <span className="font-semibold text-primary-700 dark:text-primary-400">
                  {upcomingCount}
                </span>{' '}
                task{upcomingCount !== 1 ? 's' : ''} due in the next 7 days
              </button>
            )}
          </div>
        </div>
      </div>

      <button
        onClick={handleDismiss}
        className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
        aria-label="Dismiss notification"
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  );
};
